// src/modules/products/hooks/useProducts.ts
import {
  useState,
  useEffect,
  useCallback,
  useRef,
  useContext,
  useMemo,
} from "react";
import { AuthContext } from "@modules/auth/contexts/auth-plus.context";
import { ProductService } from "../services/product.service";
import {
  ProductItem,
  ShopDto,
  BrandDto,
  UserInfo,
  CreateProductInput,
  UpdateProductInput,
} from "../models/product.model";
import { updateEntityInList } from "@modules/shared/utils/state-updates";

export function useProducts() {
  const { auth, setAuth, logout } = useContext(AuthContext);
  const service = useRef(new ProductService()).current;
  const loadingRef = useRef(false);

  const [products, setProducts] = useState<ProductItem[]>([]);
  const [shops, setShops] = useState<ShopDto[]>([]);
  const [brands, setBrands] = useState<BrandDto[]>([]);
  const [userMap, setUserMap] = useState<Map<string, UserInfo>>(new Map());
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const userMapRef = useRef(userMap);
  userMapRef.current = userMap;

  const authArgs = useMemo(
    () => ({ auth, setAuth, logout }),
    [auth, setAuth, logout],
  );

  const loadUserInfo = useCallback(
    async (userId: string) => {
      if (!userId || userMapRef.current.has(userId)) return;
      const user = await service.getUserById(userId, authArgs);
      if (user) {
        setUserMap((prev) => new Map(prev).set(userId, user));
      }
    },
    [service, authArgs],
  );

  const loadData = useCallback(async () => {
    if (!auth || loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const [allProducts, allShops, allBrands] = await Promise.all([
        service.getAllItems(authArgs),
        service.getAllShops(authArgs),
        service.getAllBrands(authArgs),
      ]);
      setProducts(allProducts);
      setShops(allShops);
      setBrands(allBrands);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load data");
    } finally {
      setLoaded(true);
      setLoading(false);
      loadingRef.current = false;
    }
  }, [auth, service, authArgs]);

  useEffect(() => {
    if (auth && !loaded) loadData();
  }, [auth, loaded, loadData]);

  // Actions
  const createProduct = useCallback(
    async (input: CreateProductInput) => {
      const newItem = await service.createItem(input, authArgs);
      setProducts((prev) => updateEntityInList(prev, newItem));
      return newItem;
    },
    [service, authArgs],
  );

  const updateProduct = useCallback(
    async (id: string, input: Omit<UpdateProductInput, "item_id">) => {
      const updated = await service.updateItem(
        { item_id: id, ...input },
        authArgs,
      );
      setProducts((prev) => updateEntityInList(prev, updated));
      return updated;
    },
    [service, authArgs],
  );

  const deleteProduct = useCallback(
    async (id: string) => {
      const success = await service.deleteItem(id, authArgs);
      if (success) setProducts((prev) => prev.filter((p) => p.id !== id));
      return success;
    },
    [service, authArgs],
  );

  const refresh = useCallback(() => {
    setLoaded(false);
    loadData();
  }, [loadData]);

  // Getters
  const getProductById = useCallback(
    (id: string) => products.find((p) => p.id === id),
    [products],
  );
  const getShopById = useCallback(
    (id: string) => shops.find((s) => s.id === id),
    [shops],
  );
  const getBrandById = useCallback(
    (id: string) => brands.find((b) => b.id === id),
    [brands],
  );

  return {
    products,
    shops,
    brands,
    userMap,
    loading,
    loaded,
    error,
    createProduct,
    updateProduct,
    deleteProduct,
    refresh,
    getProductById,
    getShopById,
    getBrandById,
    loadUserInfo,
  };
}
